import React, { useState, useReducer, createContext } from 'react';
import classNames from 'classnames';
import Cookies from 'js-cookie';
import initializeIcons from './icons/icon_library';

import { defaultViews } from './views/views';
import ViewSettings from './views/ViewSettings';
import ViewRouter from './items/ViewRouter';
import { itemReducer, idListReducer, IdListActions } from './items/itemReducers';
import { statusReducer } from './common/statusManager';
import {
  filterAssessments,
  filterKapaCalendars,
  filterEvents,
  filterSimResultSheets,
  filterRBSRisks,
  filterControls,
  initialFilters,
  filterPhotos,
  filterGoals,
  filterAchievements,
  filterTasks,
  filterMilestones
} from './filters/filterFunctions';
import { filterReducer } from './filters/filterReducer';
import NavBar from './nav/NavBar';
import darkClassNames from './common/darkClassNames';
import GoogleLogin from './google/login';

// Load css files for blueprintjs
import 'normalize.css';
import '@blueprintjs/core/lib/css/blueprint.css';
import '@blueprintjs/datetime/lib/css/blueprint-datetime.css';
import '@blueprintjs/popover2/lib/css/blueprint-popover2.css';
// And the app's own css as well
import './common/dst.css';
import { GoogleItemType, PromaItemType, RiskItemType } from './items/itemDefinitions';
import getItemProps from './items/getItemProps';

// Load the defined icons to be used by FontAwesome
initializeIcons();

// Set the App-wide react contexts
export const ApiContext = createContext(null);
export const ItemContext = createContext(null);
export const StatusContext = createContext(null);

/** The part of the content which is needed after the login is successful */
const AppContent = ({ loggedIn }) => {
  if (!loggedIn) {
    return null;
  }
  return (
    <div className="app_content_container">
      {/* <ViewSettings /> */}
      <ViewRouter />
    </div>
  );
};

/** The hierarchy level 0 */
const App = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginUsername, setLoginUsername] = useState('');
  const [userId, setUserId] = useState(Cookies.get('userId'));
  const [darkMode, setDarkMode] = useState(Cookies.get('DSTDarkMode') === 'true');
  const [odmToken, setOdmToken] = useState('');

  // Centrally manage the all the data context on level 0
  const [views, dispatchView] = useReducer(statusReducer, defaultViews);
  const [currentView, setCurrentView] = useState(defaultViews[0]);
  const [sideBarItemIds, dispatchSideBarItemIds] = useReducer(idListReducer, []);
  const addSideBarItemId = (id) => dispatchSideBarItemIds({ type: IdListActions.create, id });
  const [items, dispatchItem] = useReducer(itemReducer, []);

  const [filterItems, dispatchFilterItems] = useReducer(itemReducer, currentView.filterItems);
  const [filterDict, dispatchFilterDict] = useReducer(filterReducer, initialFilters);

  const filteredRiskAssessments = filterAssessments(
    items.filter((item) => item.itemType === RiskItemType.Assessment),
    filterDict
  );

  const filteredSimResultSheets = filterSimResultSheets(
    items.filter((item) => item.itemType === RiskItemType.SimResultSheet),
    filterDict
  );

  const filteredRBSRisks = filterRBSRisks(
    items.filter((item) => item.itemType === RiskItemType.RBSRisk),
    filterDict
  );

  const filteredControls = filterControls(
    items.filter((item) => item.itemType === RiskItemType.Control),
    filterDict
  );

  const filteredCalendars = filterKapaCalendars(
    items.filter((item) => item.itemType === GoogleItemType.Calendar)
  );

  const filteredEvents = filterEvents({
    items: items.filter((item) => item.itemType === GoogleItemType.Event),
    filterDict
  });

  const filteredTasks = filterTasks(items.filter((item) => item.itemType === PromaItemType.Task));
  const filteredAchievements = filterAchievements(
    items.filter((item) => item.itemType === PromaItemType.Achievement),
    filterDict
  );
  const filteredMilestones = filterMilestones(
    items.filter((item) => item.itemType === PromaItemType.Milestone)
  );
  const filteredGoals = filterGoals(
    items.filter((item) => item.itemType === PromaItemType.FinalGoal),
    filterDict
  );
  const filteredLocations = items.filter((item) => item.itemType === PromaItemType.Location);

  const filteredPeople = items.filter((item) => item.itemType === GoogleItemType.Person);
  const filteredPhotos = filterPhotos(
    items.filter((item) => item.itemType === GoogleItemType.Photo),
    filterDict,
    filterItems
  );

  const filteredHotSpots = items.filter((item) => item.itemType === GoogleItemType.HotSpot);

  const filteredItems = [].concat(
    filteredCalendars,
    filteredEvents,
    filteredPeople,
    filteredRiskAssessments,
    filteredSimResultSheets,
    filteredRBSRisks,
    filteredControls,
    filteredTasks,
    filteredAchievements,
    filteredMilestones,
    filteredGoals,
    filteredLocations,
    filteredPhotos,
    filteredHotSpots
  );

  /** All the props required to display a single item (and related functions) */
  const itemProps = getItemProps({
    currentView,
    items,
    filteredItems
  });

  // General status info fed down to items
  const isError = false;
  const isLoading = false;
  const isSyncing = false;

  /** Toggle dark mode and set related cookie */
  const toggleDarkMode = () => {
    Cookies.set('DSTDarkMode', !darkMode, { expires: 7, path: '' });
    setDarkMode(!darkMode);
  };
  const classes = classNames(darkClassNames(darkMode), 'maincontent');

  return (
    <div className={classes}>
      <StatusContext.Provider
        value={{
          darkMode,
          toggleDarkMode,
          isError,
          isLoading,
          isSyncing
        }}
      >
        <ApiContext.Provider
          value={{
            loggedIn,
            setLoggedIn,
            loginUsername,
            setLoginUsername,
            userId,
            setUserId,
            odmToken,
            setOdmToken
          }}
        >
          <ItemContext.Provider
            value={{
              views,
              dispatchView,
              currentView,
              setCurrentView,
              sideBarItemIds,
              addSideBarItemId,
              dispatchSideBarItemIds,
              // matchedIcons,
              filterItems,
              dispatchFilterItems,
              filterDict,
              dispatchFilterDict,
              items,
              dispatchItem,
              filteredItems,
              itemProps
            }}
          >
            {/* <GoogleLogin />
            <NavBar /> */}
            <AppContent loggedIn />
          </ItemContext.Provider>
        </ApiContext.Provider>
      </StatusContext.Provider>
    </div>
  );
};

export default App;
